import { Modal, Row, Typography, Progress, Col, Button, Checkbox } from 'antd';
import { useState, useEffect } from 'react';
import { FronteggContext } from '@frontegg/rest-api';
import { Axios } from 'src/lib';
import { showToastSuccess } from 'src/utilities/toast';
import { showToastError } from 'src/utilities/errortoast';
const { Title, Text } = Typography;
import {
  getTimeQuotaAction,
  getQuotaAction,
} from 'src/store/actions/quota.action';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
const UpgradeToProOPModal = ({ isVisible, setIsVisible, practitionerId }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpgraded, setIsUpgraded] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');

  const { timeQuota, quota } = useSelector((state) => state?.quotaState);

  const [timeBasedQuota, setTimeBasedQuota] = useState(0);
  const [encounterLimit, setEncounterLimit] = useState(
    quota?.encounters?.limit || 10
  );

  useEffect(() => {
    setTimeBasedQuota(timeQuota?.time_based_quota);
  }, [timeQuota?.time_based_quota]);

  useEffect(() => {
    setEncounterLimit(quota?.encounters?.limit);
  }, [quota?.encounters?.limit]);

  const handleContinue = async () => {
    if (!termsAccepted) {
      setTermsError('Please agree in order to continue');
      return;
    }
    setTermsError('');
    setIsLoading(true);
    analytics.track('Clicked Upgrade In Opie', {
      email: user?.email,
    });
    const token = FronteggContext.getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
    try {
      const result = await Axios.post(
        '/v2/practitioners/op/upgrade',
        { practitioner_id: practitionerId },
        { headers }
      );
      if (result.status < 300) {
        if (typeof timeBasedQuota === 'number') {
          dispatch(getTimeQuotaAction()).then(() => {
            setIsLoading(false);
            setIsUpgraded(true);
            showToastSuccess('Upgrade successful');
          });
        } else if (typeof encounterLimit === 'number') {
          dispatch(getQuotaAction()).then(() => {
            setIsLoading(false);
            setIsUpgraded(true);
            showToastSuccess('Upgrade successful');
          });
        }
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      showToastError('Upgrade failed');
    }
  };

  const handleCancel = () => {
    analytics.track('Clicked Cancel Upgrade In Opie', {
      email: user?.email,
    });
    setIsVisible(false);
    setTermsAccepted(false);
    setTermsError('');
  };

  const handleRefresh = () => {
    setIsVisible(false);
    setIsUpgraded(false);
    window.location.reload();
  };

  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  const isMobileView = window.innerWidth < 1260;

  return (
    <Modal
      centered={true}
      open={isVisible}
      onOk={isUpgraded ? handleRefresh : handleContinue}
      closable={false}
      width={isMobileView ? '90%' : '45%'}
      footer={null}
    >
      <div className="flex flex-col">
        <div
          style={{
            background: 'linear-gradient(to bottom, #10B981, #059669)',
            color: 'white',
            padding: '0',
          }}
          className="rounded-md"
        >
          <div style={{ padding: '24px' }}>
            <Row gutter={[0, 16]} align="middle">
              <Col>
                <img
                  src="https://images.insighthealth.ai/office-practicum/ai-solo-logo.png"
                  alt="Office Practicum Logo"
                  width={85}
                  height={85}
                />
              </Col>
              <Col flex="auto">
                <Title
                  level={4}
                  style={{ color: 'white', margin: 0, fontSize: '21px' }}
                >
                  Opie AI Premium
                </Title>
                <Text style={{ color: 'white', fontSize: '14px' }}>
                  Unlock the full potential of AI-assisted healthcare
                  documentation
                </Text>
              </Col>
            </Row>
            <Progress
              percent={100}
              format={() => (
                <Text style={{ color: 'white', fontSize: '13px' }}>
                  {typeof timeBasedQuota === 'number'
                    ? `${timeBasedQuota}/${timeBasedQuota} days used`
                    : `${encounterLimit - 1}/${encounterLimit - 1} encounters used`}
                </Text>
              )}
              strokeColor="white"
              trailColor="rgba(255,255,255,0.3)"
              style={{ marginTop: '12px' }}
            />
          </div>
        </div>

        <div className="mt-8 px-2">
          <Title level={3} style={{ marginBottom: '8px', fontSize: '25px' }}>
            Unlock Unlimited Access
          </Title>
          <Text
            style={{
              fontSize: '15px',
              display: 'block',
              marginBottom: '24px',
            }}
          >
            {typeof timeBasedQuota === 'number'
              ? `You've reached your limit of ${timeBasedQuota} days of free Opie AI
            trial. To continue using Opie AI, you can subscribe to one of our
            premium plans as outlined in your original order.`
              : typeof encounterLimit === 'number'
                ? `You've reached your limit of ${encounterLimit - 1} free Opie AI
            visits for ${currentMonth}. To continue using Opie AI before your
            monthly reset, you can subscribe to one of our premium plans as
            outlined in your original order.`
                : ''}
          </Text>
          {[
            {
              title: 'Unlimited AI Encounters',
              desc: 'Generate unlimited clinical documentation',
            },
          ].map((feature) => (
            <Row
              key={feature.title}
              gutter={12}
              align="top"
              style={{ marginBottom: '16px' }}
              wrap={false}
            >
              <Col flex="none">
                <CheckCircleOutlined
                  style={{
                    color: '#059669',
                    fontSize: '21px',
                    marginTop: '4px',
                  }}
                />
              </Col>
              <Col flex="auto">
                <Text strong style={{ display: 'block', fontSize: '15px' }}>
                  {feature.title}
                </Text>
                <Text style={{ fontSize: '13px' }}>{feature.desc}</Text>
              </Col>
            </Row>
          ))}
          <div style={{ marginTop: '24px' }}>
            {isUpgraded ? (
              <Button
                block
                onClick={handleRefresh}
                loading={isLoading}
                className="ih-button-primary"
                style={{
                  fontSize: '17px',
                }}
              >
                Refresh page
              </Button>
            ) : (
              <>
                <div
                  style={{
                    marginBottom: termsError ? '10px' : '16px',
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      if (e.target.checked) {
                        setTermsError('');
                      }
                    }}
                    style={{
                      marginRight: '12px',
                      marginTop: '4px',
                      border: '2px solid black',
                      width: '22px',
                      height: '22px',
                    }}
                  />
                  <div className="flex flex-col">
                    <Text style={{ fontSize: '13px' }}>
                      By proceeding, you acknowledge that you are authorized to
                      upgrade your account to a paid subscription and agree to
                      the associated terms and conditions.
                    </Text>
                    {termsError && (
                      <div
                        style={{
                          marginTop: '4px',
                          textAlign: 'left',
                          marginBottom: '4px',
                        }}
                      >
                        <Text style={{ color: 'red', fontSize: '12px' }}>
                          {termsError}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  className="ih-button-primary"
                  block
                  onClick={handleContinue}
                  loading={isLoading}
                  style={{
                    fontSize: '16px',
                  }}
                >
                  Upgrade to Unlimited License
                </Button>
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <Button
                    type="text"
                    onClick={handleCancel}
                    style={{ color: '#4B5563', fontSize: '15px' }}
                  >
                    Cancel upgrade
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UpgradeToProOPModal;
